import { logger } from '@/utils/logger';
import { useSignInSignUpModal } from '@/hooks/useSignInSignUpModal';
import { useSocialLogin } from '@/hooks/useSocialLogin';
import { useSnackbarStore } from '@/stores/snackbarStore';
import { useTerms, getTermByCategory } from '@/queries/useTermQueries';
import { Icons } from '@/styles/Icon';
import { ModalTitle } from '@/styles/Typography';
import { Term } from '@/types/term_type';
import { sanitizeURL } from '@/utils/xssProtection';
import React, { useState } from 'react';
import { styled } from 'styled-components';
import { GradientButton } from '../button/Button';
import { Snackbar, useSnackbar } from '../common/Snackbar';
import { Modal } from '../modal/Modal';
import { PasswordResetRequestModal } from './PasswordResetRequestModal';
import PWChangeModal from './PWChangeModal';
import { SignInForm } from './SignInForm';
import { SignUpForm } from './SignUpForm';
import { SocialSignUpModal } from './SocialSignUpModal';
import { VerificationModal } from './VerificationModal';

interface SignInSignUpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SignInSignUpModal: React.FC<SignInSignUpModalProps> = ({ isOpen, onClose }) => {
  // UI 상태
  const [activeTab, setActiveTab] = useState<'signin' | 'signup'>('signin');
  const [showSocialSignUpModal, setShowSocialSignUpModal] = useState(false);
  const [resetPWModalOpen, setResetPWModalOpen] = useState(false);
  const [pwChangeModalOpen, setPWChangeModalOpen] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);

  // 약관 데이터 TanStack Query
  const { data: terms } = useTerms();
  const privacyTerm = getTermByCategory(terms, 'PRIVACY');
  const serviceTerm = getTermByCategory(terms, 'TERMS');

  // Hooks
  const { snackbar, hideSnackbar } = useSnackbar();
  const { showSnackbar } = useSnackbarStore();

  // 폼 관리 커스텀 훅
  const {
    formData,
    errors,
    emailSuccessMessage,
    isLoading,
    isAgreementValid,
    updateFormData,
    handleSignIn,
    handleSignUp,
    setAgreementState,
    resetForm,
    isEmailSent,
    isVerified,
    isSendingCode,
    isVerifyingCode,
    handleSendVerificationCode,
    handleVerifyCode,
  } = useSignInSignUpModal();

  // 소셜 로그인 커스텀 훅
  const {
    loading: socialLoading,
    pendingSignupData: socialPendingData,
    handleGoogleSignIn: googleSignIn,
    handleKakaoSignIn: kakaoSignIn,
  } = useSocialLogin(() => {
    handleModalClose();
  });

  const handleTermClick = (term: Term | undefined) => {
    if (!term) {
      logger.error('Term data not loaded');
      return;
    }

    // Sanitize URL before opening to prevent javascript: or data: protocol attacks
    const sanitizedUrl = sanitizeURL(term.content_url);
    if (!sanitizedUrl) {
      logger.error('Invalid URL');
      return;
    }

    window.open(sanitizedUrl, '_blank');
  };

  // 모달 닫기 핸들러
  const handleModalClose = React.useCallback(() => {
    resetForm();
    setActiveTab('signin');
    onClose();
  }, [onClose, resetForm]);

  // 탭 변경 핸들러
  const handleTabChange = (tab: 'signin' | 'signup') => {
    setActiveTab(tab);
    resetForm();
  };

  // 로그인 핸들러
  const onSignIn = async () => {
    const response = await handleSignIn();
    if (response?.success) {
      onClose();
      showSnackbar('Signed in successfully');
    }
  };

  // 회원가입 핸들러
  const onSignUp = async () => {
    const result = await handleSignUp();

    if (result.success) {
      showSnackbar(result.message || '회원가입이 완료되었습니다.');

      // 회원가입 성공 시 자동 로그인 후 모달 닫기
      const response = await handleSignIn();
      if (response?.success) {
        showSnackbar('Signed up successfully');
      }
      onClose();

      // 페이지 새로고침 또는 홈으로 이동
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    }
  };

  // 비밀번호 재설정 핸들러
  const onPasswordReset = async () => {
    setShowPasswordResetModal(true);
  };

  // 소셜 로그인 핸들러
  const onGoogleSignIn = async () => {
    const result = await googleSignIn();
    if (result?.requiresAgreement) {
      setShowSocialSignUpModal(true);
      resetForm();
      // ✅ onClose()를 바로 호출하지 말고 상태 업데이트 후에 호출
      setTimeout(() => {
        onClose();
      }, 100);
    }
  };

  const onKakaoSignIn = async () => {
    const result = await kakaoSignIn();
    if (result?.requiresAgreement) {
      setShowSocialSignUpModal(true);
      resetForm();
      // ✅ onClose()를 바로 호출하지 말고 상태 업데이트 후에 호출
      setTimeout(() => {
        onClose();
      }, 100);
    }
  };

  const handleResetPasswordSuccess = () => {
    setResetPWModalOpen(false);
    setPWChangeModalOpen(true);
    showSnackbar('Password reset email has been sent!', { duration: 3000 });
  };

  const isSubmitDisabled = () => {
    if (activeTab === 'signin') {
      return !formData.email || !formData.password;
    } else if (activeTab === 'signup') {
      return !isVerified || !formData.password || !formData.name || !isAgreementValid;
    }
    return !isAgreementValid;
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleModalClose} size="medium">
        <ModalContent>
          <ModalTitle style={{ fontSize: '24px', marginBottom: '8px' }}>Welcome to GRAB MUSIC</ModalTitle>

          {activeTab === 'signup' && <SubTitle>Sign up to access your music collection and discover new NFTs</SubTitle>}

          <TabContainer>
            <Tab $active={activeTab === 'signin'} onClick={() => handleTabChange('signin')}>
              Sign In
            </Tab>
            <Tab $active={activeTab === 'signup'} onClick={() => handleTabChange('signup')}>
              Sign Up
            </Tab>
          </TabContainer>

          <FormContainer
            as="form"
            onSubmit={e => {
              e.preventDefault();
              activeTab === 'signin' ? handleSignIn() : handleSignUp();
            }}
          >
            {activeTab === 'signin' ? (
              <SignInForm
                formData={formData}
                errors={errors}
                onFieldChange={updateFormData}
                onForgotPassword={onPasswordReset}
              />
            ) : (
              <SignUpForm
                formData={formData}
                errors={errors}
                emailSuccessMessage={emailSuccessMessage}
                onFieldChange={updateFormData}
                onAgreementChange={setAgreementState}
                onSendVerificationCode={handleSendVerificationCode}
                onVerifyCode={handleVerifyCode}
                isEmailSent={isEmailSent}
                isVerified={isVerified}
                isSendingCode={isSendingCode}
                isVerifyingCode={isVerifyingCode}
              />
            )}

            <GradientButton
              type="submit"
              onClick={activeTab === 'signin' ? onSignIn : onSignUp}
              loading={isLoading || socialLoading}
              disabled={isSubmitDisabled()}
              width="100%"
              height="47px"
            >
              {activeTab === 'signin' ? 'Sign In' : 'Continue'}
            </GradientButton>
          </FormContainer>

          <Divider>
            <DividerLine />
            <DividerText>Or continue with</DividerText>
            <DividerLine />
          </Divider>

          <SocialButtons>
            <SocialButton onClick={onGoogleSignIn} disabled={socialLoading}>
              <img src={Icons.google} alt="Google" />
              <span>Google</span>
            </SocialButton>

            <SocialButton onClick={onKakaoSignIn} disabled={socialLoading}>
              <img src={Icons.kakao} alt="Kakao" />
              <span>Kakao</span>
            </SocialButton>
          </SocialButtons>

          <SubTitle style={{ fontSize: '16px' }}>
            By signing up, you agree to our{' '}
            <PolicyLink onClick={() => handleTermClick(serviceTerm)}>Terms of Service</PolicyLink> and{' '}
            <PolicyLink onClick={() => handleTermClick(privacyTerm)}>Privacy Policy</PolicyLink>
          </SubTitle>
        </ModalContent>
      </Modal>

      {/* 소셜 회원가입 모달 */}
      {showSocialSignUpModal && (
        <SocialSignUpModal
          isOpen={showSocialSignUpModal}
          onClose={() => setShowSocialSignUpModal(false)}
          onBack={() => {
            setShowSocialSignUpModal(false);
          }}
          pendingSignupData={socialPendingData || undefined}
        />
      )}

      {/* 비밀번호 재설정 인증 모달 */}
      {resetPWModalOpen && (
        <VerificationModal
          isOpen={resetPWModalOpen}
          onClose={() => setResetPWModalOpen(false)}
          email={formData.email}
          onSuccess={handleResetPasswordSuccess}
          mode="resetPassword"
        />
      )}

      {/* 비밀번호 재설정 요청 모달 */}
      <PasswordResetRequestModal
        isOpen={showPasswordResetModal}
        onClose={() => setShowPasswordResetModal(false)}
        onSuccess={() => {
          showSnackbar('Password reset email has been sent', {
            showCheckIcon: true,
            duration: 5000,
          });
        }}
      />

      {/* 비밀번호 변경 모달 */}
      {pwChangeModalOpen && (
        <PWChangeModal
          isOpen={pwChangeModalOpen}
          onClose={() => setPWChangeModalOpen(false)}
          email={formData.email}
          onSuccess={() => {
            showSnackbar('Password has been changed successfully. Please log in again.', {
              showCheckIcon: true,
              duration: 3000,
            });
            setPWChangeModalOpen(false);
          }}
        />
      )}

      {/* 스낵바 */}
      <Snackbar
        message={snackbar.message}
        isOpen={snackbar.isOpen}
        onClose={hideSnackbar}
        showCheckIcon={snackbar.showCheckIcon}
        duration={snackbar.duration}
      />
    </>
  );
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 4px 8px;
  width: 100%;
  height: max-content;
  box-sizing: border-box;
`;

const SubTitle = styled.p`
  color: ${({ theme }) => theme.colors.fontGray[400]};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-bottom: 40px;
`;

const TabContainer = styled.div`
  display: flex;
  width: 100%;
  height: 62px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.bgGray[300]};
  padding: 7.5px;
  margin-bottom: 40px;
  box-sizing: border-box;
`;

const Tab = styled.button<{ $active: boolean }>`
  display: flex;
  width: 261px;
  height: 47px;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  background: ${({ $active, theme }) => ($active ? theme.colors.bgWhite : 'transparent')};
  border: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  color: ${({ $active, theme }) => ($active ? theme.colors.fontBlackText : '#898989')};
  transition: all 0.3s ease;
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 533px;
  margin: 32px 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: #e1e1e1;
`;

const DividerText = styled.span`
  color: #898989;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  padding: 0 16px;
`;

const SocialButtons = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  max-width: 533px;
  margin-bottom: 40px;
`;

const SocialButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  padding: 16px 40px;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex: 1 0 0;
  border-radius: 20px;
  border: 1px solid #cecece;
  background: ${({ theme }) => theme.colors.bgWhite};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f8f8f8;
  }

  img {
    width: 24px;
    height: 24px;
  }

  span {
    font-size: 16px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.fontBlackText};
  }
`;

const PolicyLink = styled.span`
  color: #828282;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: #666;
  }
`;
